import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";

import { monthGridStart } from "../../lib/dateNav";
import {
  CALENDAR_FALLBACK_ROW_HEIGHT_PX,
  CALENDAR_VISIBLE_WEEK_ROWS,
  flippedScrollTopForWeekIndex,
  getVisibleDaysInViewport,
  maxFlippedScrollTop,
  resolveDominantMonthFromDays,
  shouldPrependOlderWeeks,
  weekIndexForDate,
} from "../today/calendarScrollLogic";

const PREPEND_BATCH_WEEKS = 8;

export type CalendarScrollRequest = { month: string; nonce: number };

export type CalendarVerticalScrollerProps = {
  weekStarts: string[];
  visibleMonth: string;
  scrollRequest: CalendarScrollRequest | null;
  selectedDate: string;
  onVisibleMonthChange: (month: string) => void;
  onPrependWeeks: (count: number) => void;
  renderWeekRow: (props: {
    weekStart: string;
    rowMeasureRef?: RefObject<HTMLDivElement | null>;
    visibleMonth: string;
  }) => ReactNode;
};

export function CalendarVerticalScroller({
  weekStarts,
  visibleMonth,
  scrollRequest,
  selectedDate,
  onVisibleMonthChange,
  onPrependWeeks,
  renderWeekRow,
}: CalendarVerticalScrollerProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const rowMeasureRef = useRef<HTMLDivElement>(null);
  const lastScrollNonceRef = useRef<number | null>(null);
  const [rowHeight, setRowHeight] = useState(0);
  const prependingRef = useRef(false);

  const layoutRowHeight = rowHeight > 0 ? rowHeight : CALENDAR_FALLBACK_ROW_HEIGHT_PX;
  const viewportHeight = layoutRowHeight * CALENDAR_VISIBLE_WEEK_ROWS;

  useLayoutEffect(() => {
    const element = rowMeasureRef.current;
    if (!element) return;
    const measure = () => setRowHeight(element.offsetHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const applyScrollTop = useCallback((scrollTop: number) => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    viewport.scrollTop = scrollTop;
  }, []);

  const scrollToWeekIndex = useCallback(
    (weekIndex: number, align: "start" | "center" | "end" = "center") => {
      if (rowHeight <= 0 || weekStarts.length === 0 || weekIndex < 0) return;
      applyScrollTop(
        flippedScrollTopForWeekIndex(
          weekIndex,
          weekStarts.length,
          layoutRowHeight,
          viewportHeight,
          align,
        ),
      );
    },
    [applyScrollTop, layoutRowHeight, rowHeight, viewportHeight, weekStarts.length],
  );

  const syncVisibleMonthFromScroll = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport || rowHeight <= 0 || weekStarts.length === 0) return;
    const visibleDays = getVisibleDaysInViewport(
      weekStarts,
      viewport.scrollTop,
      layoutRowHeight,
      viewportHeight,
    );
    onVisibleMonthChange(resolveDominantMonthFromDays(visibleDays));
  }, [layoutRowHeight, onVisibleMonthChange, rowHeight, viewportHeight, weekStarts]);

  useLayoutEffect(() => {
    if (rowHeight <= 0 || weekStarts.length === 0) return;
    if (prependingRef.current) {
      prependingRef.current = false;
      syncVisibleMonthFromScroll();
      return;
    }
    if (!scrollRequest || scrollRequest.nonce === lastScrollNonceRef.current) return;
    lastScrollNonceRef.current = scrollRequest.nonce;
    const monthWeekIndex = weekIndexForDate(weekStarts, monthGridStart(scrollRequest.month));
    if (monthWeekIndex >= 0) {
      scrollToWeekIndex(monthWeekIndex, "start");
      syncVisibleMonthFromScroll();
      return;
    }
    const selectedWeekIndex = weekIndexForDate(weekStarts, selectedDate);
    if (selectedWeekIndex >= 0) {
      scrollToWeekIndex(selectedWeekIndex, "center");
      syncVisibleMonthFromScroll();
    }
  }, [
    rowHeight,
    scrollRequest,
    scrollToWeekIndex,
    selectedDate,
    syncVisibleMonthFromScroll,
    weekStarts,
  ]);

  const handleScroll = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport || rowHeight <= 0 || weekStarts.length === 0) return;
    if (prependingRef.current) return;

    const scrollTop = viewport.scrollTop;
    const maxScroll = maxFlippedScrollTop(weekStarts.length, layoutRowHeight, viewportHeight);
    syncVisibleMonthFromScroll();

    if (shouldPrependOlderWeeks(scrollTop, maxScroll, layoutRowHeight) && weekStarts.length > 0) {
      prependingRef.current = true;
      onPrependWeeks(PREPEND_BATCH_WEEKS);
    }
  }, [layoutRowHeight, onPrependWeeks, syncVisibleMonthFromScroll, rowHeight, viewportHeight, weekStarts]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    handleScroll();
    viewport.addEventListener("scroll", handleScroll, { passive: true });
    return () => viewport.removeEventListener("scroll", handleScroll);
  }, [handleScroll, weekStarts]);

  return (
    <div
      ref={viewportRef}
      className="overflow-y-auto overscroll-y-contain"
      style={{
        height: viewportHeight,
        transform: "scaleY(-1)",
        touchAction: "pan-y",
      }}
    >
      <div style={{ transform: "scaleY(-1)" }}>
        {weekStarts.map((weekStart, index) => (
          <Fragment key={weekStart}>
            {renderWeekRow({
              weekStart,
              rowMeasureRef: index === 0 ? rowMeasureRef : undefined,
              visibleMonth,
            })}
          </Fragment>
        ))}
      </div>
    </div>
  );
}
